import { useContext, useEffect, useState } from 'react';

import { Header as AkHeader } from '@appkit4/react-components/header';
import { Avatar } from '@appkit4/react-components/avatar';
import { DropdownButton, ValueType } from '@appkit4/react-components/dropdown';
import { Badge } from '@appkit4/react-components/badge';
import { Input } from '@appkit4/react-components/field';
import { Tooltip } from '@appkit4/react-components/tooltip';

import { useMatch, useNavigate } from 'react-router-dom';

import { isUserAdmin, getInitials } from '@utils/users';

import { SessionContext } from '@contexts/SessionProvider';
import { UserContext } from '@contexts/UserProvider';

import styles from './Header.module.scss';
import { renameSessionTopic, duplicateSession } from '@config/getSessionApis';
import { Preferences } from '../Preferences';
import { PreferencesContext } from '@contexts/PreferencesProvider';
import ManageUsers from '../ManageUsers/ManageUsers';
import DesignSystemBadge from '../DesignSystemBadge';
import ShareModal from '../ShareModal/ShareModal';
import DuplicateModal from '../DuplicateModal/DuplicateModal';
import {
    GithubConnectPatModal,
    GithubEditPatModal,
    GithubPublishModal,
    GithubLeaseFailedModal,
    GithubAuthFailedBanner,
} from '../Github';

const Header = () => {
    const navigate = useNavigate();
    const isSessionPage = useMatch('/session');

    const {
        sessionName,
        sessionId,
        isSessionOwner,
        setSessionName,
        designSystem,
        templateVersion,
        setThread,
        setHistory,
        setCurrentSource,
        setSessionOwnerEmail,
        isGenerateComplete,
        githubLinked,
        setGithubLinked,
        refreshGithubSessionStatus,
    } = useContext(SessionContext);

    const { user, userEmail, userGivenName, userLastName, clearUser, githubConnected, refreshGithubCredentialStatus } =
        useContext(UserContext);

    const [sessionNameEdit, setSessionNameEdit] = useState(false);
    const [initials, setInitials] = useState('');
    const [topic, setTopic] = useState(sessionName);
    const { setShowPreferences } = useContext(PreferencesContext);
    const [isShowManageUsers, setIsShowManageUsers] = useState(false);
    const [shareModalVisible, setShareModalVisible] = useState(false);
    const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);

    const [githubConnectModalVisible, setGithubConnectModalVisible] = useState(false);
    const [githubEditPatModalVisible, setGithubEditPatModalVisible] = useState(false);
    const [githubPublishModalVisible, setGithubPublishModalVisible] = useState(false);
    const [githubLeaseFailedModalVisible, setGithubLeaseFailedModalVisible] = useState(false);

    useEffect(() => {
        if (userGivenName && userLastName) {
            const payload = {
                givenName: userGivenName,
                lastName: userLastName,
            };

            setInitials(getInitials(payload));
        }
    }, [userGivenName, userLastName]);

    // Refresh GitHub credential status once on mount when user is logged in
    useEffect(() => {
        if (userEmail) {
            refreshGithubCredentialStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEmail]);

    // Refresh GitHub session status when on a session page and sessionId is available.
    // Reset to null immediately on sessionId change so stale state from a previous
    // session never shows the wrong menu label while the new fetch is in-flight.
    useEffect(() => {
        if (isSessionPage && sessionId) {
            setGithubLinked(null);
            refreshGithubSessionStatus(sessionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSessionPage, sessionId]);

    const handleUserMenu = (value: ValueType) => {
        if (value === 'preferences') {
            setShowPreferences(true);
        } else if (value === 'manageUsers') {
            setIsShowManageUsers(true);
        } else if (value === 'manageAdmin') {
            navigate('/admins');
        } else if (value === 'analytics') {
            navigate('/analytics');
        } else if (value === 'logout') {
            clearUser();
            navigate('/login');
        } else if (value === 'github-connect') {
            setGithubConnectModalVisible(true);
        } else if (value === 'github-settings') {
            setGithubEditPatModalVisible(true);
        } else if (value === 'github-publish') {
            setGithubPublishModalVisible(true);
        } else {
            navigate(`/${value}`);
        }
    };

    const handleChange = (value: string) => {
        setTopic(value);
    };

    const onSelectResources = (value: any) => {
        if (value === 'best-practices') {
            window.open(
                'https://pwc.sharepoint.com/:w:/r/sites/GBL-IFS-appkit-hub/Shared%20Documents/RC%20Best%20practices%20.docx?d=wc286246083074bdb8813f46df221ca25&csf=1&web=1&e=I6iGhM',
                '_blank'
            );
        }
    };

    const handleBlur = (event: any) => {
        if (event.target.value.trim() === '') {
            setTopic(sessionName);
            setSessionNameEdit(false);
            return;
        }

        setSessionNameEdit(false);
        renameSessionTopic(userEmail, sessionId, topic.trim());
        setSessionName(topic.trim());
    };

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter') {
            if (event.target.value.trim() === '') {
                setTopic(sessionName);
                setSessionNameEdit(false);
                return;
            }

            setSessionNameEdit(false);
            renameSessionTopic(userEmail, sessionId, topic.trim());
            setSessionName(topic.trim());
        }
    };

    const buildMenuData = () => {
        const baseItems = isUserAdmin(user)
            ? [
                  { label: 'Preferences', value: 'preferences', disabled: false },
                  { label: 'Dashboard', value: 'dashboard', disabled: false },
                  { label: 'Manage users', value: 'manageUsers', disabled: false },
                  { label: 'Manage admins', value: 'manageAdmin', disabled: false },
                  { label: 'Analytics', value: 'analytics', disabled: false },
                  { label: 'Log out', value: 'logout', disabled: false },
              ]
            : [
                  { label: 'Preferences', value: 'preferences', disabled: false },
                  { label: 'Dashboard', value: 'dashboard', disabled: false },
                  { label: 'Log out', value: 'logout', disabled: false },
              ];

        const githubItems: { label: string; value: string; disabled: boolean }[] = [];

        if (!githubConnected) {
            githubItems.push({ label: 'Authenticate GitHub with PAT', value: 'github-connect', disabled: false });
        } else {
            githubItems.push({ label: 'GitHub settings', value: 'github-settings', disabled: false });
            if (isSessionPage) {
                if (githubLinked == null) {
                    // Status fetch is in-flight — show a disabled placeholder so the
                    // menu never flashes the wrong label while waiting for the API.
                    githubItems.push({
                        label: 'GitHub repository (loading...)',
                        value: 'github-publish',
                        disabled: true,
                    });
                } else if (githubLinked) {
                    githubItems.push({ label: 'Push to GitHub repository', value: 'github-publish', disabled: false });
                } else {
                    githubItems.push({
                        label: 'Connect to GitHub repository',
                        value: 'github-publish',
                        disabled: false,
                    });
                }
            }
        }

        return [...baseItems, ...githubItems];
    };

    const renderAvatar = () => {
        return (
            <DropdownButton
                align="right"
                customTriggerNode={true}
                splitButton={false}
                onSelect={handleUserMenu}
                data={buildMenuData()}>
                <Avatar label={initials} role="button" disabled={false} />
            </DropdownButton>
        );
    };

    const renderTitle = () => {
        return (
            <>
                <span className={styles.title}>Design Draft</span>
                <Badge value="Beta" size="sm" type="primary" />
            </>
        );
    };

    const renderSubTitle = () => {
        const isGenerating = !isGenerateComplete;
        const getDropdownOptions = () => {
            if (isSessionOwner) {
                return [
                    { label: 'Rename', value: 'rename', disabled: isGenerating },
                    { label: 'Duplicate', value: 'duplicate', disabled: isGenerating },
                ];
            } else {
                return [{ label: 'Duplicate', value: 'duplicate', disabled: isGenerating }];
            }
        };

        return (
            <div className={styles.subtitle}>
                {isSessionOwner && sessionNameEdit ? (
                    <Input
                        type="text"
                        value={topic}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onBlur={(e: any) => handleBlur(e)}
                        inputProps={{
                            autoFocus: true,
                        }}
                        hideTitleOnInput
                        className={styles['session-input']}
                    />
                ) : (
                    <div className={styles['title-select-wrapper']}>
                        <DropdownButton
                            className={styles['title-dropdown-button']}
                            kind="text"
                            data={getDropdownOptions()}
                            onSelect={handleTitleAction}
                            itemTemplate={renderDropdownItem}
                            ariaLabel="Session actions">
                            <div className={styles['button-content']}>
                                <span className={styles['session-title']}>{topic}</span>
                                <span className="Appkit4-icon icon-circle-more-outline"></span>
                            </div>
                        </DropdownButton>
                        {sessionId &&
                            (designSystem === 'Appkit' ||
                                designSystem === 'AppkitRebrand' ||
                                designSystem === 'Fluent' ||
                                designSystem === 'MaterialUI' ||
                                designSystem === 'NewEra') && (
                                <DesignSystemBadge designSystem={designSystem} size="md" />
                            )}
                    </div>
                )}
            </div>
        );
    };

    const handleManageUsersCancel = () => {
        setIsShowManageUsers(false);
    };

    const handleTitleAction = (value: any) => {
        if (!isGenerateComplete) {
            return;
        }

        if (value === 'rename') {
            setSessionNameEdit(true);
        } else if (value === 'duplicate') {
            handleDuplicate();
        }
    };

    const handleDuplicate = async () => {
        try {
            setDuplicateModalVisible(true);
            const response = await duplicateSession(sessionId);

            if (response && response.code === 1 && response.resultObject && response.resultObject.sessionId) {
                const newSessionId = response.resultObject.sessionId;

                setSessionName(sessionName + ' (Copy)');
                setThread([]);
                setHistory([]);
                setCurrentSource({});
                setSessionOwnerEmail(response.resultObject.email || userEmail);

                setDuplicateModalVisible(false);

                navigate('/dashboard');
                setTimeout(() => {
                    navigate(
                        `/session?query=get&sessionId=${newSessionId}&designSystem=${designSystem}&templateVersion=${templateVersion}`
                    );
                }, 10);
            } else {
                console.error('Unexpected response format:', response);
                setDuplicateModalVisible(false);
            }
        } catch (error) {
            console.error('Error duplicating session:', error);
            setDuplicateModalVisible(false);
        }
    };

    const handleShareModalCancel = () => {
        setShareModalVisible(false);
    };

    const handleDuplicateModalCancel = () => {
        setDuplicateModalVisible(false);
    };

    const renderDropdownItem = (label: React.ReactNode) => {
        return <div className={styles['dropdown-item']}>{label}</div>;
    };

    return (
        <>
            <AkHeader
                className={styles.header}
                onClickLogo={() => navigate('/dashboard')}
                titleTemplate={() => renderTitle()}
                optionsTemplate={() => {
                    return (
                        <div className={styles.headerOptions}>
                            <DropdownButton
                                kind="text"
                                data={[{ label: 'Best practices', value: 'best-practices' }]}
                                onSelect={onSelectResources}
                                className="resources-selector"
                                ariaLabel="Sort sessions by these options button">
                                <Tooltip
                                    trigger="hover"
                                    position="top"
                                    distance={4}
                                    id="ResourcesTooltip"
                                    content="Resources">
                                    <span className="Appkit4-icon icon-management-outline"></span>
                                </Tooltip>
                            </DropdownButton>

                            <div className="settings-icon-wrapper" tabIndex={0}>
                                <Tooltip
                                    trigger="hover"
                                    position="top"
                                    distance={4}
                                    id="FeedbackTooltip"
                                    content="Feedback">
                                    <span
                                        className="Appkit4-icon icon-comment-outline icon-primary ap-font-16 ap-container-16"
                                        onClick={() =>
                                            window.open(
                                                'https://forms.office.com/Pages/ResponsePage.aspx?id=oJQyUSA-skGpcG0wvxVG-nfuspVAq2pNrkOuaARFqBFURTJTUjNDM0dZVzhHU1IwREkyT1dHVVIySi4u',
                                                '_blank'
                                            )
                                        }
                                        role="button"
                                        aria-label="Settings"
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    );
                }}
                subTitleTemplate={isSessionPage ? () => renderSubTitle() : undefined}
                userTemplate={() => renderAvatar()}
            />
            <GithubAuthFailedBanner onUpdateToken={() => setGithubEditPatModalVisible(true)} />
            <Preferences></Preferences>
            <ManageUsers isShow={isShowManageUsers} onCancel={handleManageUsersCancel}></ManageUsers>
            {shareModalVisible && (
                <ShareModal
                    visible={shareModalVisible}
                    title="Share project"
                    onCancel={handleShareModalCancel}
                    modalStyle={{ width: '500px' }}
                    activeSessionId={sessionId}
                />
            )}
            {duplicateModalVisible && (
                <DuplicateModal
                    visible={duplicateModalVisible}
                    title="Duplicating file"
                    onCancel={handleDuplicateModalCancel}
                    modalStyle={{ width: '440px' }}
                />
            )}
            {githubConnectModalVisible && (
                <GithubConnectPatModal
                    visible={githubConnectModalVisible}
                    onCancel={() => setGithubConnectModalVisible(false)}
                />
            )}
            {githubEditPatModalVisible && (
                <GithubEditPatModal
                    visible={githubEditPatModalVisible}
                    onCancel={() => setGithubEditPatModalVisible(false)}
                />
            )}
            {githubPublishModalVisible && (
                <GithubPublishModal
                    visible={githubPublishModalVisible}
                    onCancel={() => setGithubPublishModalVisible(false)}
                    onLeaseFailure={() => {
                        setGithubPublishModalVisible(false);
                        setGithubLeaseFailedModalVisible(true);
                    }}
                />
            )}
            {githubLeaseFailedModalVisible && (
                <GithubLeaseFailedModal
                    visible={githubLeaseFailedModalVisible}
                    onCancel={() => setGithubLeaseFailedModalVisible(false)}
                />
            )}
        </>
    );
};

export default Header;
